/**
 * useNavigationStepDetector - Detects clicks on navigation elements during tutorial
 * Auto-advances tutorial when user clicks profile button or hours tab
 */

import { useEffect } from 'react';
import { useTutorial } from '@/contexts/TutorialContext';
import { useLocation, useNavigate } from 'react-router-dom';

export const useNavigationStepDetector = () => {
  const { isActive, currentStepData, nextStep } = useTutorial();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isActive || !currentStepData) return;

    // Guard to prevent double-advancing when both pointer and click fire
    let hasAdvanced = false;
    const advanceOnce = () => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      nextStep();
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Step 1: Detect profile button click
      if (currentStepData.id === 'click-profile') {
        const profileButton = target.closest('[data-testid="nav-profile-button"]');
        if (profileButton) {
          // Wait for navigation to complete, then advance
          setTimeout(() => {
            if (window.location.pathname === '/shop-profile') {
              advanceOnce();
            }
          }, 500);
        }
      }

      // Step 4: Detect hours tab click
      if (currentStepData.id === 'click-hours-tab') {
        const hoursTab = target.closest('[data-testid="shopprofile-tab-hours"]');
        if (hoursTab) {
          // Wait for URL update, then advance
          setTimeout(() => {
            const params = new URLSearchParams(window.location.search);
            if (params.get('tab') === 'hours') {
              advanceOnce();
            }
          }, 300);
        }
      }

      // Step 7: Detect culture tab click
      if (currentStepData.id === 'click-culture-tab') {
        const cultureTab = target.closest('[data-testid="shopprofile-tab-culture"]');
        if (cultureTab) {
          // Wait for URL update, then advance
          setTimeout(() => {
            const params = new URLSearchParams(window.location.search);
            if (params.get('tab') === 'culture') {
              advanceOnce();
            }
          }, 300);
        }
      }

      // Step 9: Detect save profile button click
      if (currentStepData.id === 'save-profile') {
        const saveButton = target.closest('[data-testid="business-hours-save"]');
        if (saveButton) {
          // The save button triggers form submit, which saves data and then navigates after 200ms
          // We need to wait for both the save operation AND the navigation
          // Check repeatedly until navigation happens or timeout
          let attempts = 0;
          const maxAttempts = 20; // 4 seconds total (20 * 200ms)
          
          const checkNavigation = () => {
            attempts++;
            if (window.location.pathname === '/') {
              advanceOnce();
            } else if (attempts < maxAttempts) {
              setTimeout(checkNavigation, 200);
            }
          };
          
          setTimeout(checkNavigation, 500); // Start checking after initial delay
        }
      }

      // Step 10: Detect birdseye button click
      if (currentStepData.id === 'click-birdseye') {
        const birdseyeButton = target.closest('[data-testid="birdseye-link"]');
        if (birdseyeButton) {
          // Wait for navigation to complete, then advance
          setTimeout(() => {
            if (window.location.pathname === '/birdseye') {
              advanceOnce();
            }
          }, 500);
        }
      }

      // User Tutorial: Detect overview tab click
      if (currentStepData.id === 'overview-tab') {
        const overviewTab = target.closest('[data-testid="overview-tab"]');
        if (overviewTab) {
          setTimeout(() => advanceOnce(), 300);
        }
      }

      // User Tutorial: Detect create ticket button click
      if (currentStepData.id === 'create-ticket-button') {
        const createBtn = target.closest('[data-testid="create-ticket-btn"]');
        if (createBtn) {
          setTimeout(() => {
            if (window.location.pathname === '/create-ticket') {
              advanceOnce();
            }
          }, 500);
        }
      }

      // Step 11: Detect configure layout button click
      if (currentStepData.id === 'click-configure-layout') {
        const configureButton = target.closest('[data-testid="configure-layout-btn"]');
        if (configureButton) {
          // Wait for navigation to complete, then advance
          setTimeout(() => {
            if (window.location.pathname === '/shop-floor/editor') {
              advanceOnce();
            }
          }, 500);
        }
      }

      // Step 12: create-bay - auto-advance after add bay button click
      if (currentStepData.id === 'create-bay') {
        const addBayBtn = target.closest('[data-testid="add-bay-button"]');
        if (addBayBtn) {
          setTimeout(() => advanceOnce(), 500);
        }
      }

      // Step 13: edit-bay - user manually advances with Next button after reviewing

      // Step 14: Detect Team flow start. The top-bar Team button was
      // removed in the Stage 2 cleanup; Team management is reached
      // through Shop Profile → Teams tab. We advance the tutorial when
      // the user opens Shop Profile (matches the click-team coachmark
      // which now points at `nav-profile-button`) and is heading toward
      // either `/shop-profile` or the legacy `/team-management` route.
      if (currentStepData.id === 'click-team') {
        const profileEntry = target.closest('[data-testid="nav-profile-button"]')
          || target.closest('[data-testid="nav-team-button"]');
        if (profileEntry) {
          setTimeout(() => {
            const path = window.location.pathname;
            if (path === '/team-management' || path === '/shop-profile') {
              advanceOnce();
            }
          }, 500);
        }
      }

      // Step 28: Detect hours & time off tab click
      if (currentStepData.id === 'click-hours-timeoff-tab') {
        const hoursTab = target.closest('[data-testid="tech-hours-tab"]');
        if (hoursTab) {
          // Wait for tab switch, then advance
          setTimeout(() => advanceOnce(), 300);
        }
      }

      // User Tutorial Step 18: Detect kebab menu button click
      if (currentStepData.id === 'ticket-kebab-menu') {
        const kebabButton = target.closest('[data-testid="ticket-kebab-menu"]');
        if (kebabButton) {
          // Advance immediately when button is clicked
          setTimeout(() => advanceOnce(), 50);
        }
      }

      // User Tutorial Step 19: Detect "Move to Parking Lot" click
      if (currentStepData.id === 'kebab-menu-options') {
        const menuItem = target.closest('[role="menuitem"]');
        if (menuItem && menuItem.textContent?.includes('Move to Parking Lot')) {
          // Advance when "Move to Parking Lot" is clicked
          setTimeout(() => advanceOnce(), 300);
        }
      }

      // User Tutorial Step 20: Detect specialty bays tab click
      if (currentStepData.id === 'specialty-bays-intro') {
        const specialtyTab = target.closest('[data-testid="specialty-bays-tab"]');
        if (specialtyTab) {
          // Wait for tab switch, then advance
          setTimeout(() => {
            const params = new URLSearchParams(window.location.search);
            if (params.get('tab') === 'specialty-bays') {
              advanceOnce();
            }
          }, 300);
        }
      }

      // User Tutorial Step 22: Detect parking lot tab click (from Specialty Bays or explicit Parking Lot Tab step)
      if (currentStepData.id === 'specialty-bays-view' || currentStepData.id === 'parking-lot-tab') {
        const parkingLotTab = target.closest('[data-testid="parking-lot-tab"]');
        if (parkingLotTab) {
          // Wait for tab switch, then advance
          setTimeout(() => {
            const params = new URLSearchParams(window.location.search);
            if (params.get('tab') === 'parking-lot') {
              advanceOnce();
            }
          }, 300);
        }
      }

      // User Tutorial Step 25: Detect "Move Job" from parking lot kebab menu
      if (currentStepData.id === 'parking-ticket-kebab') {
        const moveJobItem = target.closest('[role="menuitem"]');
        // Check if it's a technician selection from the Move Job submenu
        if (moveJobItem && moveJobItem.querySelector('[class*="lucide-user"]')) {
          // User selected a technician - navigate to dayboard and advance
          setTimeout(() => {
            navigate('/?tab=dayboard');
            setTimeout(() => advanceOnce(), 500);
          }, 300);
        }
      }

      // User Tutorial Step 27: Detect play button click on ticket timer
      if (currentStepData.id === 'start-job-timer') {
        const playButton = target.closest('[data-testid="timer-start"]');
        if (playButton) {
          // Advance immediately when play is clicked
          advanceOnce();
        }
      }

      // User Tutorial Step 28: Detect birdseye navigation button click
      if (currentStepData.id === 'birdseye-navigation') {
        const birdseyeButton = target.closest('[data-testid="birdseye-link"]');
        if (birdseyeButton) {
          // Navigate to birdseye and then advance
          navigate('/birdseye');
          setTimeout(() => {
            advanceOnce();
          }, 500);
        }
      }

      // User Tutorial Step 31/32: Detect deleted tickets or analytics navigation
      if (currentStepData.id === 'other-pages' || currentStepData.id === 'user-tutorial-complete') {
        const deletedButton = target.closest('[data-testid="deleted-tickets-link"]');
        const analyticsButton = target.closest('[data-testid="analytics-link"]');
        
        if (deletedButton) {
          navigate('/deleted-tickets');
          // Do not force-advance; allow staying on step 31/32 while exploring
        } else if (analyticsButton) {
          navigate('/analytics');
          // Do not force-advance; allow staying on step 31/32 while exploring
        }
      }

      // User Tutorial: Detect ticket number click to open Command Center
      if (currentStepData.id === 'ticket-command-center') {
        const ticketNumber = target.closest('[data-testid="ticket-number"]');
        if (ticketNumber) {
          setTimeout(() => advanceOnce(), 300);
        }
      }

      // User Tutorial: Detect technician passport link click
      if (currentStepData.id === 'technician-passport-intro') {
        const passportLink = target.closest('[data-testid="technician-passport-link"]');
        if (passportLink) {
          setTimeout(() => advanceOnce(), 500);
        }
      }

      // Manager Tutorial: Detect outdoor weather card interaction
      if (currentStepData.id === 'outdoor-weather-settings') {
        const weatherCard = target.closest('[data-testid="outdoor-weather-card"]');
        if (weatherCard) {
          setTimeout(() => advanceOnce(), 300);
        }
      }
    };

    // Some UI libraries open menus on pointerdown and prevent click.
    const handlePointerDown = (event: PointerEvent) => {
      if (currentStepData.id !== 'ticket-kebab-menu') return;
      const target = event.target as HTMLElement;
      const kebabButton = target.closest('[data-testid="ticket-kebab-menu"]');
      if (kebabButton) {
        // Advance as soon as the interaction is detected
        setTimeout(() => advanceOnce(), 0);
      }
    };

    document.addEventListener('click', handleClick, true);
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [isActive, currentStepData, nextStep, location, navigate]);
};
